/**
 * Inventory — Catalog, stock-on-hand, usage history, and low-stock alerts.
 *
 * Tabs:
 *   - Stock      — quantity-on-hand per (item × location), with row actions
 *                  to record USE / RECEIVE / ADJUST transactions.
 *   - Catalog    — CRUD for org-level inventory items (parts/spares).
 *   - Activity   — append-only ledger of every stock movement.
 *   - Alerts     — open low-stock alerts; ack/resolve and quick-mailto vendor.
 *   - Settings   — who gets notified when an item drops below its threshold.
 */

import {
	type InventoryItem,
	type InventoryLowStockAlertRow,
	type InventoryNotificationRecipientRow,
	type InventoryStockRow,
	type InventoryTransactionRow,
	type InventoryTransactionType,
	acknowledgeInventoryAlert,
	addInventoryNotificationRecipient,
	createInventoryItem,
	deleteInventoryItem,
	getInventoryStats,
	listInventoryAlerts,
	listInventoryItems,
	listInventoryNotificationRecipients,
	listInventoryStock,
	listInventoryTransactions,
	recordInventoryTransaction,
	removeInventoryNotificationRecipient,
	resolveInventoryAlert,
	updateInventoryItem,
} from "@/services/inventory-service";
import { getOrganizationLocations } from "@/services/location-service";
import { getOrganizationUsers } from "@/services/user-service";
import { getOrganizationAssetTypes } from "@/services/utility-service";
import { type Vendor, listVendors } from "@/services/work-order-service";
import { useInventoryStore } from "@/stores/inventory-store";
import {
	AppstoreOutlined,
	BellOutlined,
	BoxPlotOutlined,
	CheckOutlined,
	DeleteOutlined,
	DollarOutlined,
	EditOutlined,
	HistoryOutlined,
	MailOutlined,
	PlusOutlined,
	ReloadOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import {
	Button,
	Drawer,
	Empty,
	Form,
	Input,
	InputNumber,
	Modal,
	Popconfirm,
	Select,
	Space,
	Switch,
	Table,
	Tabs,
	Tag,
	Tooltip,
	message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Inventory.scss";

// ─── Helper formatters ──────────────────────────────────────────────────────

const formatDate = (d: string | null | undefined) =>
	d
		? new Date(d).toLocaleDateString(undefined, {
				month: "short",
				day: "numeric",
				year: "2-digit",
			})
		: "—";

const formatDateTime = (d: string | null | undefined) =>
	d
		? new Date(d).toLocaleString(undefined, {
				month: "short",
				day: "numeric",
				year: "2-digit",
				hour: "numeric",
				minute: "2-digit",
			})
		: "—";

const TX_COLORS: Record<InventoryTransactionType, string> = {
	RECEIVE: "green",
	USE: "blue",
	ADJUST: "purple",
	TRANSFER_IN: "cyan",
	TRANSFER_OUT: "orange",
};

const ALERT_STATUS_COLORS: Record<string, string> = {
	OPEN: "red",
	ACKNOWLEDGED: "orange",
	RESOLVED: "default",
};

// ─── Main component ─────────────────────────────────────────────────────────

const Inventory = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	// Allow deep-linking from the location workspace / single-location page so
	// the page lands pre-filtered: ?locationId=<id>&tab=<stock|activity|alerts|...>
	const initialLocationId = (() => {
		const raw = searchParams.get("locationId");
		const n = raw ? Number(raw) : Number.NaN;
		return Number.isFinite(n) && n > 0 ? n : undefined;
	})();
	const initialTab = (() => {
		const t = searchParams.get("tab");
		return t && ["stock", "catalog", "activity", "alerts", "settings"].includes(t)
			? t
			: "stock";
	})();

	const [activeTab, setActiveTab] = useState<string>(initialTab);

	const {
		items,
		itemsTotal,
		itemsLoading,
		itemFilters,
		stock,
		stockTotal,
		stockLoading,
		stockFilters,
		transactions,
		transactionsTotal,
		transactionsLoading,
		alerts,
		alertsTotal,
		alertsLoading,
		stats,
		setItems,
		setItemFilters,
		setItemsLoading,
		setStock,
		setStockFilters,
		setStockLoading,
		setTransactions,
		setTransactionsLoading,
		setAlerts,
		setAlertsLoading,
		setStats,
		upsertItemInList,
		removeItemFromList,
	} = useInventoryStore();

	// Reference data shared across tabs / forms
	const [locations, setLocations] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [vendors, setVendors] = useState<Vendor[]>([]);
	const [assetTypes, setAssetTypes] = useState<
		Array<{ id: number; name: string }>
	>([]);
	const [orgUsers, setOrgUsers] = useState<
		Array<{ id: number; name: string; email: string | null }>
	>([]);
	const [recipients, setRecipients] = useState<
		InventoryNotificationRecipientRow[]
	>([]);

	// Modals
	const [itemModalOpen, setItemModalOpen] = useState(false);
	const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
	const [txDrawerOpen, setTxDrawerOpen] = useState(false);
	const [txDrawerCtx, setTxDrawerCtx] = useState<{
		stockRow?: InventoryStockRow;
		txType: InventoryTransactionType;
	} | null>(null);
	// "Add Stock" modal — used to seed inventory at a (item, location) pair
	// that has never been stocked before. Submits as a RECEIVE transaction so
	// the activity ledger stays consistent with all other stock movements.
	const [addStockModalOpen, setAddStockModalOpen] = useState(false);
	const [addStockPrefill, setAddStockPrefill] = useState<{
		itemId?: number;
		locationId?: number;
	} | null>(null);

	// ─── Initial reference loads ──────────────────────────────────────────
	useEffect(() => {
		(async () => {
			try {
				const [locs, vList, aTypes, uList] = await Promise.all([
					getOrganizationLocations().catch(() => []),
					listVendors(false).catch(() => ({ vendors: [] })),
					getOrganizationAssetTypes(false).catch(() => []),
					getOrganizationUsers().catch(() => []),
				]);
				setLocations(
					(locs ?? []).map((l: any) => ({
						id: l.id,
						name: l.name ?? l.locationName ?? `#${l.id}`,
					})),
				);
				setVendors(vList.vendors ?? []);
				setAssetTypes(
					(aTypes ?? []).map((a: any) => ({ id: a.id, name: a.name })),
				);
				setOrgUsers(
					(uList ?? []).map((u: any) => ({
						id: u.id,
						name: u.name ?? u.email,
						email: u.email ?? null,
					})),
				);
			} catch (e) {
				console.error("Failed to load inventory reference data", e);
			}
		})();
	}, []);

	// ─── Stats ───────────────────────────────────────────────────────────
	const refreshStats = useCallback(async () => {
		try {
			const s = await getInventoryStats();
			setStats(s);
		} catch (e) {
			console.error("Failed to load inventory stats", e);
		}
	}, [setStats]);

	useEffect(() => {
		refreshStats();
	}, [refreshStats]);

	// ─── Stock fetch ─────────────────────────────────────────────────────
	const fetchStock = useCallback(async () => {
		setStockLoading(true);
		try {
			const result = await listInventoryStock({
				locationId: stockFilters.locationId ?? "",
				itemId: stockFilters.itemId ?? "",
				lowStockOnly: stockFilters.lowStockOnly,
				search: stockFilters.search ?? "",
				page: stockFilters.page,
				limit: stockFilters.limit,
			});
			setStock(result.stock, result.total);
		} catch (e: any) {
			console.error(e);
			message.error(e.message ?? "Failed to load stock");
		} finally {
			setStockLoading(false);
		}
	}, [stockFilters, setStock, setStockLoading]);

	useEffect(() => {
		if (activeTab === "stock") fetchStock();
	}, [activeTab, fetchStock]);

	// ─── Items fetch ─────────────────────────────────────────────────────
	const fetchItems = useCallback(async () => {
		setItemsLoading(true);
		try {
			const result = await listInventoryItems({
				search: itemFilters.search ?? "",
				category: itemFilters.category ?? "",
				page: itemFilters.page,
				limit: itemFilters.limit,
				sortBy: itemFilters.sortBy,
				sortOrder: itemFilters.sortOrder,
			});
			setItems(result.items, result.total);
		} catch (e: any) {
			console.error(e);
			message.error(e.message ?? "Failed to load items");
		} finally {
			setItemsLoading(false);
		}
	}, [itemFilters, setItems, setItemsLoading]);

	useEffect(() => {
		if (activeTab === "catalog") fetchItems();
	}, [activeTab, fetchItems]);

	// ─── Transactions fetch ──────────────────────────────────────────────
	const [txFilters, setTxFilters] = useState<{
		itemId?: number;
		locationId?: number;
		transactionType?: InventoryTransactionType;
		page: number;
		limit: number;
	}>({ page: 1, limit: 50, locationId: initialLocationId });

	// On first mount, push the URL's locationId into the persisted Stock filters
	// so the Stock tab opens pre-filtered (zustand store outlives the URL).
	useEffect(() => {
		if (initialLocationId && stockFilters.locationId !== initialLocationId) {
			setStockFilters({ locationId: initialLocationId, page: 1 });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchTransactions = useCallback(async () => {
		setTransactionsLoading(true);
		try {
			const result = await listInventoryTransactions({
				itemId: txFilters.itemId ?? "",
				locationId: txFilters.locationId ?? "",
				transactionType: txFilters.transactionType ?? "",
				page: txFilters.page,
				limit: txFilters.limit,
			});
			setTransactions(result.transactions, result.total);
		} catch (e: any) {
			console.error(e);
			message.error(e.message ?? "Failed to load activity");
		} finally {
			setTransactionsLoading(false);
		}
	}, [txFilters, setTransactions, setTransactionsLoading]);

	useEffect(() => {
		if (activeTab === "activity") fetchTransactions();
	}, [activeTab, fetchTransactions]);

	// ─── Alerts fetch ────────────────────────────────────────────────────
	const [alertStatusFilter, setAlertStatusFilter] = useState<string>("OPEN");

	const fetchAlerts = useCallback(async () => {
		setAlertsLoading(true);
		try {
			const result = await listInventoryAlerts({
				status: alertStatusFilter,
				page: 1,
				limit: 100,
			});
			setAlerts(result.alerts, result.total);
		} catch (e: any) {
			console.error(e);
			message.error(e.message ?? "Failed to load alerts");
		} finally {
			setAlertsLoading(false);
		}
	}, [alertStatusFilter, setAlerts, setAlertsLoading]);

	useEffect(() => {
		if (activeTab === "alerts") fetchAlerts();
	}, [activeTab, fetchAlerts]);

	// ─── Settings (recipients) fetch ─────────────────────────────────────
	const fetchRecipients = useCallback(async () => {
		try {
			const r = await listInventoryNotificationRecipients();
			setRecipients(r.recipients);
		} catch (e: any) {
			console.error(e);
		}
	}, []);

	useEffect(() => {
		if (activeTab === "settings") fetchRecipients();
	}, [activeTab, fetchRecipients]);

	// ─── Handlers: items ─────────────────────────────────────────────────
	const handleSaveItem = async (values: any) => {
		try {
			if (editingItem) {
				const updated = await updateInventoryItem(editingItem.id, values);
				upsertItemInList(updated);
				message.success("Item updated");
			} else {
				const created = await createInventoryItem(values);
				upsertItemInList(created);
				message.success("Item created");
			}
			setItemModalOpen(false);
			setEditingItem(null);
			refreshStats();
		} catch (e: any) {
			message.error(e.message ?? "Failed to save item");
		}
	};

	const handleDeleteItem = async (id: number) => {
		try {
			await deleteInventoryItem(id);
			removeItemFromList(id);
			message.success("Item deleted");
			refreshStats();
		} catch (e: any) {
			message.error(e.message ?? "Failed to delete item");
		}
	};

	// ─── Handlers: transactions ──────────────────────────────────────────
	const handleRecordTransaction = async (values: any) => {
		try {
			const txType = txDrawerCtx?.txType ?? "USE";
			const stockRow = txDrawerCtx?.stockRow;
			if (!stockRow) {
				message.error("No stock row selected");
				return;
			}
			const rawQty = Math.abs(Number(values.quantity));
			const signed =
				txType === "USE" || txType === "TRANSFER_OUT" ? -rawQty : rawQty;

			const result = await recordInventoryTransaction({
				itemId: stockRow.inventoryItemId,
				locationId: stockRow.locationId,
				transactionType: txType,
				quantityDelta: signed,
				assetId: values.assetId ?? null,
				notes: values.notes,
			});
			if (result.alertCreated) {
				message.warning(
					"Stock now at or below reorder threshold — an alert was created.",
				);
			} else {
				message.success("Transaction recorded");
			}
			setTxDrawerOpen(false);
			setTxDrawerCtx(null);
			fetchStock();
			refreshStats();
		} catch (e: any) {
			message.error(e.message ?? "Failed to record transaction");
		}
	};

	// ─── Handlers: add stock ─────────────────────────────────────────────
	const handleAddStock = async (values: {
		itemId: number;
		locationId: number;
		quantity: number;
		notes?: string;
	}) => {
		try {
			const qty = Math.abs(Number(values.quantity));
			if (!qty) {
				message.error("Quantity must be greater than 0");
				return;
			}
			const result = await recordInventoryTransaction({
				itemId: values.itemId,
				locationId: values.locationId,
				transactionType: "RECEIVE",
				quantityDelta: qty,
				notes: values.notes,
			});
			message.success(
				`Added ${qty} unit${qty === 1 ? "" : "s"} (${result.stock.quantityOnHand} on hand)`,
			);
			setAddStockModalOpen(false);
			setAddStockPrefill(null);
			fetchStock();
			refreshStats();
		} catch (e: any) {
			message.error(e.message ?? "Failed to add stock");
		}
	};

	// ─── Handlers: alerts ────────────────────────────────────────────────
	const handleAcknowledge = async (id: number) => {
		try {
			await acknowledgeInventoryAlert(id);
			message.success("Alert acknowledged");
			fetchAlerts();
			refreshStats();
		} catch (e: any) {
			message.error(e.message ?? "Failed to acknowledge");
		}
	};
	const handleResolve = async (id: number) => {
		try {
			await resolveInventoryAlert(id);
			message.success("Alert resolved");
			fetchAlerts();
			refreshStats();
		} catch (e: any) {
			message.error(e.message ?? "Failed to resolve");
		}
	};

	const buildVendorMailto = (alert: InventoryLowStockAlertRow) => {
		const email = alert.preferredVendorEmail ?? "";
		const subject = encodeURIComponent(`Reorder request: ${alert.itemName}`);
		const body = encodeURIComponent(
			`Hello${alert.preferredVendorName ? ` ${alert.preferredVendorName}` : ""},\n\n` +
				`We'd like to place a reorder for the following item:\n\n` +
				`Item: ${alert.itemName}\n` +
				`Location: ${alert.locationName}\n` +
				`Current quantity on hand: ${alert.quantityAtAlert} ${alert.itemUnit}\n` +
				`Reorder threshold: ${alert.thresholdAtAlert} ${alert.itemUnit}\n\n` +
				`Please confirm availability and lead time.\n\nThank you.`,
		);
		return `mailto:${email}?subject=${subject}&body=${body}`;
	};

	// ─── Handlers: recipients ────────────────────────────────────────────
	const [newRecipientUser, setNewRecipientUser] = useState<
		number | undefined
	>();
	const [newRecipientLocation, setNewRecipientLocation] = useState<
		number | "ORG_WIDE"
	>("ORG_WIDE");

	const handleAddRecipient = async () => {
		if (!newRecipientUser) {
			message.warning("Pick a user first");
			return;
		}
		try {
			await addInventoryNotificationRecipient({
				userId: newRecipientUser,
				locationId:
					newRecipientLocation === "ORG_WIDE" ? null : newRecipientLocation,
			});
			message.success("Recipient added");
			setNewRecipientUser(undefined);
			fetchRecipients();
		} catch (e: any) {
			message.error(e.message ?? "Failed to add recipient");
		}
	};
	const handleRemoveRecipient = async (id: number) => {
		try {
			await removeInventoryNotificationRecipient(id);
			fetchRecipients();
		} catch (e: any) {
			message.error(e.message ?? "Failed to remove recipient");
		}
	};

	// ─── Columns: Stock ──────────────────────────────────────────────────
	const stockColumns: ColumnsType<InventoryStockRow> = [
		{
			title: "Item",
			dataIndex: "itemName",
			key: "itemName",
			render: (name: string, row) => (
				<Button
					type="link"
					style={{ padding: 0, fontWeight: 600 }}
					onClick={() => navigate(`/inventory/items/${row.inventoryItemId}`)}
				>
					{name}
				</Button>
			),
		},
		{
			title: "SKU",
			dataIndex: "itemSku",
			key: "itemSku",
			width: 130,
			render: (v) => v ?? "—",
		},
		{
			title: "Location",
			dataIndex: "locationName",
			key: "locationName",
			width: 180,
			ellipsis: true,
		},
		{
			title: "On Hand",
			dataIndex: "quantityOnHand",
			key: "quantityOnHand",
			width: 110,
			align: "right",
			render: (q: number, row) => (
				<span style={{ fontFamily: "monospace", fontWeight: 600 }}>
					{q} {row.itemUnit}
				</span>
			),
		},
		{
			title: "Threshold",
			key: "threshold",
			width: 100,
			align: "right",
			render: (_, row) => row.effectiveThreshold ?? "—",
		},
		{
			title: "Status",
			key: "status",
			width: 110,
			render: (_, row) =>
				row.isLowStock ? (
					<Tag color="orange" icon={<WarningOutlined />}>
						LOW
					</Tag>
				) : (
					<Tag color="green">OK</Tag>
				),
		},
		{
			title: "Vendor",
			dataIndex: "preferredVendorName",
			key: "preferredVendorName",
			width: 160,
			ellipsis: true,
			render: (v) => v ?? <span style={{ color: "#bbb" }}>—</span>,
		},
		{
			title: "Actions",
			key: "actions",
			width: 240,
			render: (_, row) => (
				<Space size={4}>
					<Tooltip title="Record use / take-out">
						<Button
							size="small"
							onClick={() => {
								setTxDrawerCtx({ stockRow: row, txType: "USE" });
								setTxDrawerOpen(true);
							}}
						>
							Use
						</Button>
					</Tooltip>
					<Tooltip title="Record stock received">
						<Button
							size="small"
							onClick={() => {
								setTxDrawerCtx({ stockRow: row, txType: "RECEIVE" });
								setTxDrawerOpen(true);
							}}
						>
							Receive
						</Button>
					</Tooltip>
					<Tooltip title="Manual count adjustment">
						<Button
							size="small"
							onClick={() => {
								setTxDrawerCtx({ stockRow: row, txType: "ADJUST" });
								setTxDrawerOpen(true);
							}}
						>
							Adjust
						</Button>
					</Tooltip>
				</Space>
			),
		},
	];

	// ─── Columns: Catalog ────────────────────────────────────────────────
	const catalogColumns: ColumnsType<InventoryItem> = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			render: (name: string, row) => (
				<Button
					type="link"
					style={{ padding: 0, fontWeight: 600 }}
					onClick={() => navigate(`/inventory/items/${row.id}`)}
				>
					{name}
				</Button>
			),
		},
		{
			title: "SKU",
			dataIndex: "sku",
			key: "sku",
			width: 130,
			render: (v) => v ?? "—",
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			width: 140,
			render: (v) => (v ? <Tag>{v}</Tag> : "—"),
		},
		{ title: "Unit", dataIndex: "unit", key: "unit", width: 80 },
		{
			title: "Unit Cost",
			dataIndex: "unitCost",
			key: "unitCost",
			width: 110,
			align: "right",
			render: (c: number | null) =>
				c == null ? "—" : `$${Number(c).toFixed(2)}`,
		},
		{
			title: "Total On Hand",
			dataIndex: "totalQuantityOnHand",
			key: "totalQuantityOnHand",
			width: 130,
			align: "right",
			render: (q: number = 0, row) => (
				<span style={{ fontFamily: "monospace" }}>
					{q} {row.unit}
				</span>
			),
		},
		{
			title: "Locations",
			dataIndex: "locationsStocked",
			key: "locationsStocked",
			width: 100,
			align: "right",
			render: (n: number = 0) => n,
		},
		{
			title: "Vendor",
			dataIndex: "preferredVendorName",
			key: "preferredVendorName",
			width: 150,
			ellipsis: true,
			render: (v: string | null | undefined) => v ?? "—",
		},
		{
			title: "Reorder At",
			dataIndex: "defaultReorderThreshold",
			key: "defaultReorderThreshold",
			width: 110,
			align: "right",
			render: (v: number | null) => v ?? "—",
		},
		{
			title: "Actions",
			key: "actions",
			width: 130,
			render: (_, row) => (
				<Space size={4}>
					<Button
						size="small"
						icon={<EditOutlined />}
						onClick={() => {
							setEditingItem(row);
							setItemModalOpen(true);
						}}
					/>
					<Popconfirm
						title="Delete this item?"
						description="This soft-deletes the catalog entry. Stock and history are preserved."
						onConfirm={() => handleDeleteItem(row.id)}
					>
						<Button size="small" icon={<DeleteOutlined />} danger />
					</Popconfirm>
				</Space>
			),
		},
	];

	// ─── Columns: Activity ───────────────────────────────────────────────
	const activityColumns: ColumnsType<InventoryTransactionRow> = [
		{
			title: "When",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 160,
			render: formatDateTime,
		},
		{
			title: "Type",
			dataIndex: "transactionType",
			key: "transactionType",
			width: 130,
			render: (t: InventoryTransactionType) => (
				<Tag color={TX_COLORS[t]}>{t.replace("_", " ")}</Tag>
			),
		},
		{
			title: "Item",
			dataIndex: "itemName",
			key: "itemName",
			render: (name: string, row) => (
				<Button
					type="link"
					style={{ padding: 0 }}
					onClick={() => navigate(`/inventory/items/${row.inventoryItemId}`)}
				>
					{name}
				</Button>
			),
		},
		{
			title: "Location",
			dataIndex: "locationName",
			key: "locationName",
			width: 160,
			ellipsis: true,
		},
		{
			title: "Qty",
			dataIndex: "quantityDelta",
			key: "quantityDelta",
			width: 90,
			align: "right",
			render: (n: number, row) => (
				<span
					style={{
						color: n > 0 ? "#52c41a" : n < 0 ? "#ff4d4f" : undefined,
						fontFamily: "monospace",
						fontWeight: 600,
					}}
				>
					{n > 0 ? "+" : ""}
					{n} {row.itemUnit}
				</span>
			),
		},
		{
			title: "After",
			dataIndex: "quantityAfter",
			key: "quantityAfter",
			width: 80,
			align: "right",
			render: (n: number) => (
				<span style={{ fontFamily: "monospace" }}>{n}</span>
			),
		},
		{
			title: "Asset",
			dataIndex: "assetTag",
			key: "assetTag",
			width: 120,
			render: (v: string | null) => v ?? "—",
		},
		{
			title: "By",
			dataIndex: "performedByUserName",
			key: "performedByUserName",
			width: 140,
			render: (v) => v ?? "—",
		},
		{
			title: "Notes",
			dataIndex: "notes",
			key: "notes",
			ellipsis: true,
			render: (v) => v ?? "",
		},
	];

	// ─── Columns: Alerts ─────────────────────────────────────────────────
	const alertColumns: ColumnsType<InventoryLowStockAlertRow> = [
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 130,
			render: (s: string) => <Tag color={ALERT_STATUS_COLORS[s]}>{s}</Tag>,
		},
		{ title: "Item", dataIndex: "itemName", key: "itemName" },
		{
			title: "Location",
			dataIndex: "locationName",
			key: "locationName",
			width: 180,
			ellipsis: true,
		},
		{
			title: "On Hand",
			dataIndex: "quantityAtAlert",
			key: "quantityAtAlert",
			width: 100,
			align: "right",
			render: (n: number, row) => (
				<span
					style={{ color: "#ff4d4f", fontFamily: "monospace", fontWeight: 600 }}
				>
					{n} {row.itemUnit}
				</span>
			),
		},
		{
			title: "Threshold",
			dataIndex: "thresholdAtAlert",
			key: "thresholdAtAlert",
			width: 100,
			align: "right",
			render: (n: number) => (
				<span style={{ fontFamily: "monospace" }}>{n}</span>
			),
		},
		{
			title: "Vendor",
			dataIndex: "preferredVendorName",
			key: "preferredVendorName",
			width: 170,
			ellipsis: true,
			render: (v) => v ?? "—",
		},
		{
			title: "Triggered",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 130,
			render: formatDate,
		},
		{
			title: "Actions",
			key: "actions",
			width: 320,
			render: (_, row) => (
				<Space size={4} wrap>
					{row.preferredVendorEmail && (
						<Button
							size="small"
							icon={<MailOutlined />}
							href={buildVendorMailto(row)}
							type="primary"
							ghost
						>
							Email Vendor
						</Button>
					)}
					{row.status === "OPEN" && (
						<Button
							size="small"
							icon={<CheckOutlined />}
							onClick={() => handleAcknowledge(row.id)}
						>
							Acknowledge
						</Button>
					)}
					{row.status !== "RESOLVED" && (
						<Button size="small" onClick={() => handleResolve(row.id)}>
							Resolve
						</Button>
					)}
				</Space>
			),
		},
	];

	// ─── Render ──────────────────────────────────────────────────────────
	return (
		<div className="inventory-page">
			{/* Stats */}
			{stats && (
				<div className="inv-stats-bar">
					<div className="inv-stat-card inv-stat-items">
						<div className="inv-stat-icon">
							<AppstoreOutlined />
						</div>
						<div className="inv-stat-body">
							<span className="inv-stat-num">{stats.totalItems}</span>
							<span className="inv-stat-lbl">Catalog Items</span>
						</div>
					</div>
					<div className="inv-stat-card inv-stat-stock">
						<div className="inv-stat-icon">
							<BoxPlotOutlined />
						</div>
						<div className="inv-stat-body">
							<span className="inv-stat-num">{stats.totalQuantityOnHand}</span>
							<span className="inv-stat-lbl">Units On Hand</span>
						</div>
					</div>
					<div className="inv-stat-card inv-stat-low">
						<div className="inv-stat-icon">
							<WarningOutlined />
						</div>
						<div className="inv-stat-body">
							<span className="inv-stat-num">{stats.totalLowStockRows}</span>
							<span className="inv-stat-lbl">Low Stock</span>
						</div>
					</div>
					<div className="inv-stat-card inv-stat-alerts">
						<div className="inv-stat-icon">
							<BellOutlined />
						</div>
						<div className="inv-stat-body">
							<span className="inv-stat-num">{stats.openAlerts}</span>
							<span className="inv-stat-lbl">Open Alerts</span>
						</div>
					</div>
					<div className="inv-stat-card inv-stat-value">
						<div className="inv-stat-icon">
							<DollarOutlined />
						</div>
						<div className="inv-stat-body">
							<span className="inv-stat-num">
								${Number(stats.estimatedInventoryValue).toLocaleString()}
							</span>
							<span className="inv-stat-lbl">Estimated Value</span>
						</div>
					</div>
				</div>
			)}

			{/* Toolbar */}
			<div className="inv-toolbar">
				<h2>
					<AppstoreOutlined /> Inventory
				</h2>
			</div>

			{/* Tabs */}
			<Tabs
				activeKey={activeTab}
				onChange={setActiveTab}
				items={[
					// ─── Stock ───
					{
						key: "stock",
						label: (
							<span>
								<BoxPlotOutlined /> Stock
							</span>
						),
						children: (
							<div className="inv-tab-body">
								<Space
									style={{
										marginBottom: 12,
										width: "100%",
										justifyContent: "space-between",
										flexWrap: "wrap",
									}}
								>
									<Space wrap>
										<Input.Search
											placeholder="Search item / SKU / location"
											allowClear
											style={{ width: 240 }}
											defaultValue={stockFilters.search}
											onSearch={(v) => setStockFilters({ search: v, page: 1 })}
										/>
										<Select
											placeholder="Location"
											allowClear
											style={{ width: 200 }}
											value={stockFilters.locationId}
											onChange={(v) =>
												setStockFilters({ locationId: v, page: 1 })
											}
											options={locations.map((l) => ({
												value: l.id,
												label: l.name,
											}))}
										/>
										<Switch
											checkedChildren="Low only"
											unCheckedChildren="All"
											checked={stockFilters.lowStockOnly}
											onChange={(v) =>
												setStockFilters({ lowStockOnly: v, page: 1 })
											}
										/>
									</Space>
									<Space>
										<Button icon={<ReloadOutlined />} onClick={fetchStock}>
											Refresh
										</Button>
										<Button
											type="primary"
											icon={<PlusOutlined />}
											onClick={() => {
												setAddStockPrefill({
													locationId: stockFilters.locationId,
												});
												setAddStockModalOpen(true);
											}}
										>
											Add Stock
										</Button>
									</Space>
								</Space>
								<Table
									columns={stockColumns}
									dataSource={stock}
									rowKey="id"
									loading={stockLoading}
									rowClassName={(r) => (r.isLowStock ? "inv-row-low" : "")}
									pagination={{
										current: stockFilters.page,
										pageSize: stockFilters.limit,
										total: stockTotal,
										showSizeChanger: true,
										showTotal: (t) => `${t} stock rows`,
										onChange: (page, pageSize) =>
											setStockFilters({ page, limit: pageSize }),
									}}
									scroll={{ x: 1100 }}
									size="middle"
								/>
							</div>
						),
					},

					// ─── Catalog ───
					{
						key: "catalog",
						label: (
							<span>
								<AppstoreOutlined /> Catalog
							</span>
						),
						children: (
							<div className="inv-tab-body">
								<Space
									style={{
										marginBottom: 12,
										width: "100%",
										justifyContent: "space-between",
										flexWrap: "wrap",
									}}
								>
									<Space wrap>
										<Input.Search
											placeholder="Search name / SKU / description"
											allowClear
											style={{ width: 280 }}
											defaultValue={itemFilters.search}
											onSearch={(v) => setItemFilters({ search: v, page: 1 })}
										/>
										<Input
											placeholder="Category"
											allowClear
											style={{ width: 160 }}
											defaultValue={itemFilters.category}
											onPressEnter={(e: any) =>
												setItemFilters({ category: e.target.value, page: 1 })
											}
										/>
									</Space>
									<Space>
										<Button icon={<ReloadOutlined />} onClick={fetchItems}>
											Refresh
										</Button>
										<Button
											type="primary"
											icon={<PlusOutlined />}
											onClick={() => {
												setEditingItem(null);
												setItemModalOpen(true);
											}}
										>
											Add Item
										</Button>
									</Space>
								</Space>
								<Table
									columns={catalogColumns}
									dataSource={items}
									rowKey="id"
									loading={itemsLoading}
									pagination={{
										current: itemFilters.page,
										pageSize: itemFilters.limit,
										total: itemsTotal,
										showSizeChanger: true,
										showTotal: (t) => `${t} items`,
										onChange: (page, pageSize) =>
											setItemFilters({ page, limit: pageSize }),
									}}
									scroll={{ x: 1200 }}
									size="middle"
								/>
							</div>
						),
					},

					// ─── Activity ───
					{
						key: "activity",
						label: (
							<span>
								<HistoryOutlined /> Activity
							</span>
						),
						children: (
							<div className="inv-tab-body">
								<Space
									style={{
										marginBottom: 12,
										width: "100%",
										justifyContent: "space-between",
										flexWrap: "wrap",
									}}
								>
									<Space wrap>
										<Select
											placeholder="Item"
											allowClear
											style={{ width: 220 }}
											showSearch
											optionFilterProp="label"
											value={txFilters.itemId}
											onChange={(v) =>
												setTxFilters((p) => ({ ...p, itemId: v, page: 1 }))
											}
											options={items.map((i) => ({
												value: i.id,
												label: i.name,
											}))}
										/>
										<Select
											placeholder="Location"
											allowClear
											style={{ width: 200 }}
											value={txFilters.locationId}
											onChange={(v) =>
												setTxFilters((p) => ({ ...p, locationId: v, page: 1 }))
											}
											options={locations.map((l) => ({
												value: l.id,
												label: l.name,
											}))}
										/>
										<Select
											placeholder="Type"
											allowClear
											style={{ width: 160 }}
											value={txFilters.transactionType}
											onChange={(v) =>
												setTxFilters((p) => ({
													...p,
													transactionType: v,
													page: 1,
												}))
											}
											options={[
												{ value: "RECEIVE", label: "Receive" },
												{ value: "USE", label: "Use" },
												{ value: "ADJUST", label: "Adjust" },
												{ value: "TRANSFER_IN", label: "Transfer In" },
												{ value: "TRANSFER_OUT", label: "Transfer Out" },
											]}
										/>
									</Space>
									<Button icon={<ReloadOutlined />} onClick={fetchTransactions}>
										Refresh
									</Button>
								</Space>
								<Table
									columns={activityColumns}
									dataSource={transactions}
									rowKey="id"
									loading={transactionsLoading}
									pagination={{
										current: txFilters.page,
										pageSize: txFilters.limit,
										total: transactionsTotal,
										showSizeChanger: true,
										showTotal: (t) => `${t} transactions`,
										onChange: (page, pageSize) =>
											setTxFilters((p) => ({ ...p, page, limit: pageSize })),
									}}
									scroll={{ x: 1100 }}
									size="middle"
								/>
							</div>
						),
					},

					// ─── Alerts ───
					{
						key: "alerts",
						label: (
							<span>
								<WarningOutlined /> Alerts
							</span>
						),
						children: (
							<div className="inv-tab-body">
								<Space style={{ marginBottom: 12 }}>
									<Select
										value={alertStatusFilter}
										onChange={setAlertStatusFilter}
										style={{ width: 180 }}
										options={[
											{ value: "OPEN", label: "Open" },
											{ value: "ACKNOWLEDGED", label: "Acknowledged" },
											{ value: "RESOLVED", label: "Resolved" },
										]}
									/>
									<Button icon={<ReloadOutlined />} onClick={fetchAlerts}>
										Refresh
									</Button>
								</Space>
								<Table
									columns={alertColumns}
									dataSource={alerts}
									rowKey="id"
									loading={alertsLoading}
									pagination={false}
									scroll={{ x: 1100 }}
									size="middle"
								/>
							</div>
						),
					},

					// ─── Settings ───
					{
						key: "settings",
						label: (
							<span>
								<BellOutlined /> Settings
							</span>
						),
						children: (
							<div className="inv-tab-body">
								<h3 style={{ marginTop: 0 }}>Notification recipients</h3>
								<p style={{ color: "#888", marginBottom: 16 }}>
									Subscribed users get an in-app notification (and an entry in
									the bell menu) the first time an item drops to or below its
									reorder threshold at the chosen location. Pick "All locations"
									to be notified org-wide.
								</p>

								<Space wrap style={{ marginBottom: 16 }}>
									<Select
										placeholder="Pick a user"
										style={{ width: 240 }}
										showSearch
										optionFilterProp="label"
										value={newRecipientUser}
										onChange={(v) => setNewRecipientUser(v)}
										options={orgUsers.map((u) => ({
											value: u.id,
											label: u.name,
										}))}
									/>
									<Select
										value={newRecipientLocation}
										onChange={(v) => setNewRecipientLocation(v)}
										style={{ width: 240 }}
										options={[
											{ value: "ORG_WIDE", label: "All locations (org-wide)" },
											...locations.map((l) => ({ value: l.id, label: l.name })),
										]}
									/>
									<Button
										type="primary"
										icon={<PlusOutlined />}
										onClick={handleAddRecipient}
									>
										Subscribe
									</Button>
								</Space>

								{recipients.length === 0 ? (
									<Empty description="No recipients yet — low-stock alerts will still be visible on the Alerts tab, but nobody will be notified by the bell menu." />
								) : (
									<Table
										dataSource={recipients}
										rowKey="id"
										pagination={false}
										size="small"
										columns={[
											{ title: "User", dataIndex: "userName" },
											{
												title: "Email",
												dataIndex: "userEmail",
												render: (v) => v ?? "—",
											},
											{
												title: "Scope",
												dataIndex: "locationName",
												render: (v) =>
													v ? (
														<Tag>{v}</Tag>
													) : (
														<Tag color="purple">All locations</Tag>
													),
											},
											{
												title: "Subscribed",
												dataIndex: "createdAt",
												width: 140,
												render: formatDate,
											},
											{
												title: "Actions",
												key: "actions",
												width: 100,
												render: (_, r) => (
													<Popconfirm
														title="Remove this recipient?"
														onConfirm={() => handleRemoveRecipient(r.id)}
													>
														<Button
															size="small"
															danger
															icon={<DeleteOutlined />}
														/>
													</Popconfirm>
												),
											},
										]}
									/>
								)}
							</div>
						),
					},
				]}
			/>

			{/* Item create/edit modal */}
			<ItemModal
				open={itemModalOpen}
				onClose={() => {
					setItemModalOpen(false);
					setEditingItem(null);
				}}
				onSave={handleSaveItem}
				editing={editingItem}
				vendors={vendors}
				assetTypes={assetTypes}
			/>

			{/* Transaction drawer */}
			<TransactionDrawer
				open={txDrawerOpen}
				onClose={() => {
					setTxDrawerOpen(false);
					setTxDrawerCtx(null);
				}}
				onSubmit={handleRecordTransaction}
				ctx={txDrawerCtx}
			/>

			{/* Add stock modal */}
			<AddStockModal
				open={addStockModalOpen}
				onClose={() => {
					setAddStockModalOpen(false);
					setAddStockPrefill(null);
				}}
				onSubmit={handleAddStock}
				locations={locations}
				prefill={addStockPrefill}
			/>
		</div>
	);
};

// ─── ItemModal ──────────────────────────────────────────────────────────────

interface ItemModalProps {
	open: boolean;
	onClose: () => void;
	onSave: (values: any) => void;
	editing: InventoryItem | null;
	vendors: Vendor[];
	assetTypes: Array<{ id: number; name: string }>;
}

const ItemModal: React.FC<ItemModalProps> = ({
	open,
	onClose,
	onSave,
	editing,
	vendors,
	assetTypes,
}) => {
	const [form] = Form.useForm();

	useEffect(() => {
		if (open) {
			if (editing) {
				form.setFieldsValue({
					name: editing.name,
					description: editing.description,
					sku: editing.sku,
					unit: editing.unit,
					unitCost: editing.unitCost ?? undefined,
					category: editing.category,
					preferredVendorId: editing.preferredVendorId ?? undefined,
					compatibleAssetTypeIds: editing.compatibleAssetTypeIds ?? [],
					defaultReorderThreshold: editing.defaultReorderThreshold ?? undefined,
					defaultReorderQuantity: editing.defaultReorderQuantity ?? undefined,
				});
			} else {
				form.resetFields();
				form.setFieldsValue({ unit: "each" });
			}
		}
	}, [open, editing, form]);

	return (
		<Modal
			open={open}
			title={editing ? `Edit: ${editing.name}` : "Add inventory item"}
			onCancel={onClose}
			onOk={() => form.submit()}
			okText={editing ? "Save changes" : "Create item"}
			width={640}
			destroyOnClose
		>
			<Form form={form} layout="vertical" onFinish={onSave}>
				<Form.Item
					label="Name"
					name="name"
					rules={[{ required: true, message: "Name is required" }]}
				>
					<Input placeholder="e.g. Condenser fan motor 1/4 HP" />
				</Form.Item>
				<Form.Item label="Description" name="description">
					<Input.TextArea rows={2} placeholder="Optional spec notes" />
				</Form.Item>
				<Space style={{ width: "100%" }} size="middle" wrap>
					<Form.Item label="SKU" name="sku" style={{ flex: 1, minWidth: 140 }}>
						<Input placeholder="Optional" />
					</Form.Item>
					<Form.Item
						label="Unit"
						name="unit"
						style={{ flex: 1, minWidth: 100 }}
					>
						<Input placeholder="each / box / ft" />
					</Form.Item>
					<Form.Item
						label="Unit cost ($)"
						name="unitCost"
						style={{ flex: 1, minWidth: 120 }}
					>
						<InputNumber
							min={0}
							step={0.01}
							style={{ width: "100%" }}
							placeholder="Optional"
						/>
					</Form.Item>
					<Form.Item
						label="Category"
						name="category"
						style={{ flex: 1, minWidth: 140 }}
					>
						<Input placeholder="HVAC / Refrigeration / Electrical / …" />
					</Form.Item>
				</Space>
				<Form.Item label="Preferred vendor" name="preferredVendorId">
					<Select
						allowClear
						showSearch
						optionFilterProp="label"
						placeholder="Pick from work-order vendor list"
						options={vendors.map((v) => ({ value: v.id, label: v.name }))}
					/>
				</Form.Item>
				<Form.Item
					label="Compatible asset types"
					name="compatibleAssetTypeIds"
					tooltip="Items the system will offer when servicing one of these asset types."
				>
					<Select
						mode="multiple"
						allowClear
						showSearch
						optionFilterProp="label"
						placeholder="e.g. Walk-In Cooler, Condenser Unit"
						options={assetTypes.map((a) => ({ value: a.id, label: a.name }))}
					/>
				</Form.Item>
				<Space style={{ width: "100%" }} size="middle" wrap>
					<Form.Item
						label="Default reorder threshold"
						name="defaultReorderThreshold"
						tooltip="Trigger an alert when the on-hand quantity at any location falls to or below this number. Per-location override available on each stock row."
						style={{ flex: 1, minWidth: 180 }}
					>
						<InputNumber
							min={0}
							style={{ width: "100%" }}
							placeholder="Leave blank to disable"
						/>
					</Form.Item>
					<Form.Item
						label="Default reorder qty"
						name="defaultReorderQuantity"
						style={{ flex: 1, minWidth: 180 }}
					>
						<InputNumber
							min={0}
							style={{ width: "100%" }}
							placeholder="How many to reorder"
						/>
					</Form.Item>
				</Space>
			</Form>
		</Modal>
	);
};

// ─── TransactionDrawer ─────────────────────────────────────────────────────

interface TransactionDrawerProps {
	open: boolean;
	onClose: () => void;
	onSubmit: (values: any) => void;
	ctx: {
		stockRow?: InventoryStockRow;
		txType: InventoryTransactionType;
	} | null;
}

const TX_TITLES: Record<InventoryTransactionType, string> = {
	RECEIVE: "Receive stock",
	USE: "Use / take out",
	ADJUST: "Manual adjustment",
	TRANSFER_IN: "Transfer in",
	TRANSFER_OUT: "Transfer out",
};

const TransactionDrawer: React.FC<TransactionDrawerProps> = ({
	open,
	onClose,
	onSubmit,
	ctx,
}) => {
	const [form] = Form.useForm();

	useEffect(() => {
		if (open) form.resetFields();
	}, [open, form]);

	if (!ctx?.stockRow) return null;

	const stockRow = ctx.stockRow;
	const txType = ctx.txType;
	const isAdjust = txType === "ADJUST";

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title={`${TX_TITLES[txType]} — ${stockRow.itemName}`}
			width={420}
			destroyOnClose
			extra={
				<Button type="primary" onClick={() => form.submit()}>
					{txType === "USE"
						? "Confirm use"
						: txType === "RECEIVE"
							? "Confirm receive"
							: "Save"}
				</Button>
			}
		>
			<div
				style={{
					marginBottom: 16,
					padding: 12,
					background: "#fafafa",
					borderRadius: 8,
				}}
			>
				<div style={{ fontSize: 12, color: "#888" }}>Location</div>
				<div style={{ fontWeight: 600 }}>{stockRow.locationName}</div>
				<div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
					Current on hand
				</div>
				<div style={{ fontWeight: 600 }}>
					{stockRow.quantityOnHand} {stockRow.itemUnit}
					{stockRow.effectiveThreshold !== null && (
						<span
							style={{
								color: "#888",
								marginLeft: 8,
								fontWeight: 400,
								fontSize: 12,
							}}
						>
							(threshold {stockRow.effectiveThreshold})
						</span>
					)}
				</div>
			</div>
			<Form form={form} layout="vertical" onFinish={onSubmit}>
				<Form.Item
					label={isAdjust ? "Signed delta (e.g. -2 to remove 2)" : "Quantity"}
					name="quantity"
					rules={[{ required: true, message: "Required" }]}
					tooltip={
						isAdjust
							? "For adjustments enter a positive or negative integer."
							: "We'll automatically apply the correct sign for this transaction type."
					}
				>
					<InputNumber
						min={isAdjust ? undefined : 1}
						style={{ width: "100%" }}
					/>
				</Form.Item>
				{txType === "USE" && (
					<Form.Item
						label="Asset (optional)"
						name="assetId"
						tooltip="Which asset was the part used on?"
					>
						<InputNumber
							min={1}
							placeholder="Asset ID"
							style={{ width: "100%" }}
						/>
					</Form.Item>
				)}
				<Form.Item label="Notes" name="notes">
					<Input.TextArea rows={3} placeholder="Optional notes" />
				</Form.Item>
			</Form>
		</Drawer>
	);
};

// ─── AddStockModal ──────────────────────────────────────────────────────────
//
// First-time stocking flow: pick an item from the catalog, pick a location,
// enter quantity. We submit as a RECEIVE transaction so the row gets created
// (or its quantity incremented if it already exists) and the activity ledger
// reflects the addition. Threshold/alert evaluation happens server-side as
// part of the same flow.

interface AddStockModalProps {
	open: boolean;
	onClose: () => void;
	onSubmit: (values: {
		itemId: number;
		locationId: number;
		quantity: number;
		notes?: string;
	}) => void;
	locations: Array<{ id: number; name: string }>;
	prefill: { itemId?: number; locationId?: number } | null;
}

const AddStockModal: React.FC<AddStockModalProps> = ({
	open,
	onClose,
	onSubmit,
	locations,
	prefill,
}) => {
	const [form] = Form.useForm();
	const [items, setItems] = useState<InventoryItem[]>([]);
	const [itemsLoading, setItemsLoading] = useState(false);

	// Load the full catalog when the modal opens. Capped at 500; orgs with more
	// items can still type-search the dropdown to filter client-side.
	useEffect(() => {
		if (!open) return;
		setItemsLoading(true);
		listInventoryItems({
			page: 1,
			limit: 500,
			sortBy: "name",
			sortOrder: "asc",
		})
			.then((res) => {
				setItems(res.items);
			})
			.catch((e) => {
				console.error("Failed to load catalog items for Add Stock modal", e);
				message.error("Failed to load catalog items");
			})
			.finally(() => setItemsLoading(false));
	}, [open]);

	useEffect(() => {
		if (open) {
			form.resetFields();
			form.setFieldsValue({
				itemId: prefill?.itemId,
				locationId: prefill?.locationId,
				quantity: 1,
			});
		}
	}, [open, prefill, form]);

	const itemOptions = items.map((i) => ({
		value: i.id,
		label: i.sku ? `${i.name} (${i.sku})` : i.name,
	}));

	return (
		<Modal
			open={open}
			title="Add stock to a location"
			onCancel={onClose}
			onOk={() => form.submit()}
			okText="Add stock"
			width={520}
			destroyOnClose
		>
			<p style={{ color: "#888", marginTop: 0 }}>
				Records a <Tag color="green">RECEIVE</Tag> transaction. If this is the
				first time stocking this item at this location, the stock row will be
				created automatically.
			</p>
			<Form form={form} layout="vertical" onFinish={onSubmit}>
				<Form.Item
					label="Item"
					name="itemId"
					rules={[{ required: true, message: "Pick a catalog item" }]}
				>
					<Select
						showSearch
						optionFilterProp="label"
						placeholder={
							itemsLoading ? "Loading catalog…" : "Search by name or SKU"
						}
						loading={itemsLoading}
						options={itemOptions}
						notFoundContent={
							itemsLoading
								? "Loading…"
								: "No catalog items yet — add one in the Catalog tab first."
						}
					/>
				</Form.Item>
				<Form.Item
					label="Location"
					name="locationId"
					rules={[{ required: true, message: "Pick a location" }]}
				>
					<Select
						showSearch
						optionFilterProp="label"
						placeholder="Pick a location"
						options={locations.map((l) => ({ value: l.id, label: l.name }))}
					/>
				</Form.Item>
				<Form.Item
					label="Quantity received"
					name="quantity"
					rules={[
						{ required: true, message: "Quantity is required" },
						{
							validator: (_, v) =>
								Number(v) > 0
									? Promise.resolve()
									: Promise.reject(new Error("Must be greater than 0")),
						},
					]}
				>
					<InputNumber min={1} step={1} style={{ width: "100%" }} />
				</Form.Item>
				<Form.Item label="Notes" name="notes">
					<Input.TextArea
						rows={2}
						placeholder="Optional — e.g. PO #, vendor reference"
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export { Inventory };
